import type { Character, WorldElement } from '../api/types';
import { getMetaValue, setMetaValue, type LorathLocalDatabase } from './db';
import { audioRecord, characterVoice, npcVoice, sanitizeCachedCharacter, sanitizeCachedNpc } from './audioDto';

// Nur die v5-Migration setzt 0. Neue Datenbanken und normale Synchronisation
// brauchen keine zusätzlichen Requests und keinen vollständigen Bootstrap.
export async function refreshAudioDtos(db: LorathLocalDatabase, fetchCharacters: () => Promise<Character[]>, fetchElements: () => Promise<WorldElement[]>): Promise<void> {
  if (await getMetaValue<number>(db, 'audioDtoVersion', 1) !== 0) return;
  const [beforeCharacters, beforeElements] = await Promise.all([db.characters.toArray(), db.worldElements.toArray()]);
  const results = await Promise.allSettled([fetchCharacters(), fetchElements()]);
  const failed = results.find(result => result.status === 'rejected');
  if (failed?.status === 'rejected') throw failed.reason;
  const characters = (results[0] as PromiseFulfilledResult<Character[]>).value;
  const elements = (results[1] as PromiseFulfilledResult<WorldElement[]>).value;
  const characterMap = new Map(characters.map(row => [row.id, sanitizeCachedCharacter(row)]));
  const npcMap = new Map(elements.filter(row => row.type === 'npc').map(row => [row.id, sanitizeCachedNpc(row)]));
  await db.transaction('rw', [db.characters, db.worldElements, db.meta], async () => {
    for (const before of beforeCharacters) {
      const fresh = characterMap.get(before.id);
      const current = await db.characters.get(before.id);
      if (!fresh || !current || JSON.stringify(characterVoice(current)) !== JSON.stringify(characterVoice(before))) continue;
      // Nur Stimme ersetzen: laufende Textbearbeitung oder gelöschte Datensätze
      // werden vom langsamen Vollabruf weder überschrieben noch wiederhergestellt.
      await db.characters.put({ ...current, companion_data: { ...audioRecord(current.companion_data), voice: characterVoice(fresh) } } as Character);
    }
    for (const before of beforeElements) {
      const fresh = npcMap.get(before.id);
      const current = await db.worldElements.get(before.id);
      if (!fresh || !current || JSON.stringify(npcVoice(current)) !== JSON.stringify(npcVoice(before))) continue;
      const data = audioRecord(current.type_data);
      await db.worldElements.put({ ...current, type_data: { ...data, npc: { ...audioRecord(data.npc), voice: npcVoice(fresh) } } });
    }
    await setMetaValue(db, 'audioDtoVersion', 1);
  });
}
